import { useCallback, useEffect, useRef } from 'react';

/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const handlers = useSingleAndDoubleClick({
 *     onSingleClick() {
 *       console.log('single click!');
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return <button {...handlers}>Click Me!</button>;
 * }
 * ```
 */

/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * Setting `persist: true` changes the onSingleClick parameters. It will
 * be called with the MouseEvent from the first click.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const handlers = useSingleAndDoubleClick({
 *     persist: true,
 *     onSingleClick(event) {
 *       console.log(`single click: ${event.clientX}`);
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return <button {...handlers}>Click Me!</button>;
 * }
 * ```
 */

/**
 * Calls onSingleClick 500ms after a click if there is no double click.
 *
 * The 2nd argument to the `onClick` event handler returned from this hook
 * will be passed to onSingleClick.
 *
 * @example
 * ```tsx
 * function MyC() {
 *   const { onClick, onDoubleClick } = useSingleAndDoubleClick({
 *     persist: true,
 *     onSingleClick(clientX) {
 *       console.log(`single click: ${clientX}`);
 *     },
 *     onDoubleClick(event) {
 *       console.log('double click!');
 *     }
 *   });
 *   return (
 *     <button
 *       onClick={(event) => {
 *         onClick(event, event.clientX);
 *       }}
 *       onDoubleClick={onDoubleClick}
 *     >
 *       Click Me!
 *     </button>
 *   );
 * }
 * ```
 */

export function useSingleAndDoubleClick(props) {
  const refSingleClickCallback = useRef(props.onSingleClick);
  useEffect(() => {
    refSingleClickCallback.current = props.onSingleClick;
    return () => refSingleClickCallback.current = undefined;
  }, [props.onSingleClick]);
  const refClickCount = useRef(0);
  const refTimeout = useRef(undefined);
  const onClick = useCallback((event, snapshot) => {
    refClickCount.current += 1;
    if (refTimeout.current !== undefined) {
      clearTimeout(refTimeout.current);
      refTimeout.current = undefined;
    }
    if (props.persist) {
      event.persist();
      snapshot = event;
    }
    refTimeout.current = setTimeout(() => {
      refTimeout.current = undefined;
      refClickCount.current = 0;
      const onSingleClick = refSingleClickCallback.current;
      if (onSingleClick) onSingleClick(snapshot);
    }, 250);
  }, [props.persist]);
  const onDoubleClick = useCallback(event => {
    if (refClickCount.current < 2) return;
    refClickCount.current = 0;
    if (refTimeout.current === undefined) return;
    clearTimeout(refTimeout.current);
    refTimeout.current = undefined;
    const onDoubleClick = props.onDoubleClick;
    onDoubleClick(event);
  }, [props.onDoubleClick]);
  return {
    onClick,
    onDoubleClick
  };
}
//# sourceMappingURL=useSingleAndDoubleClick.js.map